import { useQueryParams } from 'shared/hooks/use-query-params';
import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import useRoleUsers from './use-role-users';
import { debounce } from 'shared/utils';
import { useUsersPagesCache } from 'modules/roles-manager/ui/hooks/use-roles-manager-provider';
import { useSelector } from 'react-redux';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';

function useUsersList() {
  const userTournamentId = useSelector(authStoreUserTournamentId);

  const { setPageCount, setPageData, pagesCache, pageCount, resetPagesCache } = useUsersPagesCache();

  const [params, setParams] = useQueryParams({
    page: { key: 'page', defaultValue: 1, parser: Number },
    status: { key: 'status', defaultValue: null, parser: Number },
    name: { key: 'name', defaultValue: null, parser: String },
    phone: { key: 'phone', defaultValue: null, parser: String },
  });

  const [storage, setStorage] = useLocalStorage({
    perPage: { key: 'usersPerPage', defaultValue: 8, parser: Number },
  });

  const { data, isLoading, isFetching, isError, ...rest } = useRoleUsers({
    tournamentId: userTournamentId ?? undefined,
    page: params.page || 1,
    perPage: storage.perPage || 8,
    filters: {
      isActive: params.status ?? undefined,
      name: params.name ?? undefined,
      phone: params.phone ?? undefined,
    },
  });

  const debouncedSetPerPage = useMemo(
    () =>
      debounce((value: number) => {
        setParams({ page: 1 });
        setStorage({ perPage: Number(value) });
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams, setStorage],
  );

  const debouncedSetName = useMemo(
    () =>
      debounce((value: string) => {
        if (value && value !== '') {
          setParams({ name: value, page: 1 });
        } else {
          setParams({ name: null, page: 1 });
        }
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams],
  );

  const debouncedSetPhone = useMemo(
    () =>
      debounce((value: string) => {
        if (value && value !== '') {
          setParams({ phone: value, page: 1 });
        } else {
          setParams({ phone: null, page: 1 });
        }
        resetPagesCache();
      }, 500),
    [resetPagesCache, setParams],
  );

  const changeFilter = useCallback(
    (key: keyof typeof params | keyof typeof storage, value: any) => {
      switch (key) {
        case 'page':
          setParams({ page: Number(value) });
          break;
        case 'perPage':
          debouncedSetPerPage(value);
          break;
        case 'status':
          if (value !== null) {
            setParams({ status: Number(value), page: 1 });
          } else {
            setParams({ status: null, page: 1 });
          }
          resetPagesCache();
          break;
        case 'name':
          debouncedSetName(value);
          break;
        case 'phone':
          debouncedSetPhone(value);
          break;
        default:
          toast.error('Неизвестный фильтр');
      }
    },
    [debouncedSetName, debouncedSetPerPage, resetPagesCache, setParams],
  );

  useEffect(() => {
    if (data && !isFetching) {
      setPageData(params.page, data);

      const maxPage = Math.max(...Object.keys(pagesCache).map(Number));
      const lastPageData = pagesCache[maxPage];
      const lastPageIsEmpty = lastPageData?.length === 0;
      const hasMore = lastPageData?.length >= storage.perPage;

      let safeMaxPage: number = maxPage;
      if (hasMore) safeMaxPage = maxPage + 1;
      else if (lastPageIsEmpty) safeMaxPage = maxPage - 1;

      setPageCount(safeMaxPage);
      if (params.page > safeMaxPage) setParams({ page: safeMaxPage });
    }
  }, [data, isFetching, pagesCache, params.page, setPageCount, setPageData, setParams, storage.perPage]);

  return {
    data: data ?? [],
    pages: pageCount,
    page: params.page,
    changeFilter,
    isLoading: { isLoading, isLoaded: !isFetching && !isLoading && !isError },
    queryState: { isLoading, isFetching, isError, ...rest },
    filters: {
      status: params.status,
      name: params.name,
      perPage: storage.perPage,
      phone: params.phone,
    },
  };
}

export default useUsersList;
